import { useAtom } from 'jotai';
import { type PointerEvent as ReactPointerEvent, useCallback, useRef } from 'react';

import {
  RIGHT_SIDEBAR_MAX_WIDTH,
  RIGHT_SIDEBAR_MIN_WIDTH,
  rightSidebarWidthAtom,
} from '@frontend/atoms';

const clampWidth = (value: number) =>
  Math.max(RIGHT_SIDEBAR_MIN_WIDTH, Math.min(RIGHT_SIDEBAR_MAX_WIDTH, value));

/**
 * Drag state lives in a ref so each pointermove uses the values captured at
 * drag start — avoids stale closures and any per-frame re-render churn.
 */
type DragState = { startX: number; startWidth: number };

export function useResizableSidebarWidth() {
  const [width, setWidth] = useAtom(rightSidebarWidthAtom);
  const dragRef = useRef<DragState | null>(null);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      dragRef.current = { startX: event.clientX, startWidth: width };

      const handleMove = (moveEvent: PointerEvent) => {
        const drag = dragRef.current;
        if (!drag) return;
        // Sidebar docks on the right; dragging the left edge LEFT widens it.
        const delta = drag.startX - moveEvent.clientX;
        setWidth(clampWidth(drag.startWidth + delta));
      };

      const handleUp = () => {
        dragRef.current = null;
        window.removeEventListener('pointermove', handleMove);
        window.removeEventListener('pointerup', handleUp);
        document.body.style.removeProperty('cursor');
        document.body.style.removeProperty('user-select');
      };

      // Pin the resize cursor and disable text selection while dragging so the
      // pointer doesn't flicker as it leaves the 4px handle.
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', handleUp);
    },
    [width, setWidth]
  );

  return { width, onPointerDown };
}
